import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

function ArticlePage() {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const articles = {
    '1': {
      id: 1,
      title: "Master Spanish  Conversation in 30 Days",
      subtitle: "The Ultimate Guide to Speaking Fluently",
      author: "María González",
      authorTitle: "Language Expert and Native Speaker",
      date: "January 10, 2024",
      readTime: "8 min read",
      image: "/images/spanish1.webp",
      category: "Learning Method",
      content: `
        <p class="text-2xl leading-relaxed text-gray-700 font-light mb-8">
          Learning to speak Spanish conversationally in 30 days might sound ambitious, but with the right approach and dedication, 
          it's absolutely achievable. The key is focusing on <strong class="text-red-600">practical, everyday conversations</strong> 
          rather than trying to master every grammatical rule.
        </p>

        <div class="my-12">
          <img src="/images/spanish2.webp" alt="Spanish Learning" class="w-full rounded-3xl shadow-2xl" />
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-red-500">
          Why 30 Days is the Perfect Timeline
        </h2>

        <p class="text-xl mb-6">
          Research in language acquisition shows that <strong>intensive, focused practice</strong> over a short period 
          is more effective than sporadic study over months. Here's why 30 days works:
        </p>

        <div class="grid md:grid-cols-2 gap-6 my-8">
          <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-2xl border-l-4 border-red-500">
            <h4 class="text-xl font-bold text-red-600 mb-3 text-left">Brain Plasticity</h4>
            <p class="text-gray-700">Your brain forms new neural pathways most efficiently with daily, consistent practice.</p>
          </div>
          <div class="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-2xl border-l-4 border-orange-500">
            <h4 class="text-xl font-bold text-orange-600 mb-3 text-left">Focused Goals</h4>
            <p class="text-gray-700">A 30-day deadline creates urgency and helps you stay motivated and committed.</p>
          </div>
          <div class="bg-gradient-to-br from-yellow-50 to-red-50 p-6 rounded-2xl border-l-4 border-yellow-600">
            <h4 class="text-xl font-bold text-yellow-700 mb-3 text-left">Habit Formation</h4>
            <p class="text-gray-700">It takes 21-30 days to form a habit. You'll build a sustainable learning routine.</p>
          </div>
          <div class="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-2xl border-l-4 border-pink-500">
            <h4 class="text-xl font-bold text-pink-600 mb-3 text-left">Momentum</h4>
            <p class="text-gray-700">Quick wins in the first week fuel your motivation for the entire journey.</p>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-orange-500">
          Week 1: Building Your Foundation
        </h2>

        <div class="bg-gradient-to-r from-red-600 to-orange-500 text-white p-8 rounded-3xl my-8 shadow-2xl">
          <h3 class="text-3xl font-bold mb-6">Days 1-3: Essential Greetings</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <h4 class="text-xl font-semibold mb-4 text-yellow-200">Master These First</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Hola</strong> - Hello</li>
                <li>• <strong>Buenos días</strong> - Good morning</li>
                <li>• <strong>Buenas tardes</strong> - Good afternoon</li>
                <li>• <strong>Buenas noches</strong> - Good evening</li>
                <li>• <strong>¿Cómo estás?</strong> - How are you?</li>
              </ul>
            </div>
            <div>
              <h4 class="text-xl font-semibold mb-4 text-yellow-200">Introduce Yourself</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Me llamo...</strong> - My name is...</li>
                <li>• <strong>Soy de...</strong> - I'm from...</li>
                <li>• <strong>Tengo... años</strong> - I'm... years old</li>
                <li>• <strong>Mucho gusto</strong> - Nice to meet you</li>
                <li>• <strong>Encantado/a</strong> - Pleased to meet you</li>
              </ul>
            </div>
          </div>
        </div>

       

        <div class="bg-white border-4 border-orange-200 p-8 rounded-3xl my-8 shadow-xl">
          <h3 class="text-3xl font-bold text-gray-900 mb-6">Days 4-7: Numbers, Time and Daily Routines</h3>
          <div class="space-y-6">
            <div class="flex items-start">
              <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">1</div>
              <div>
                <h4 class="text-xl font-bold text-gray-900 mb-2">Count from 1 to 100</h4>
                <p class="text-gray-700 text-lg">Master numbers for shopping, telling time, and everyday situations. Practice counting everything around you!</p>
              </div>
            </div>
            <div class="flex items-start">
              <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">2</div>
              <div>
                <h4 class="text-xl font-bold text-gray-900 mb-2">Tell Time in Spanish</h4>
                <p class="text-gray-700 text-lg"><strong>Son las tres</strong> (It's 3 o'clock), <strong>Es la una y media</strong> (It's 1:30). Practice every time you check the clock!</p>
              </div>
            </div>
            <div class="flex items-start">
              <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-yellow-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">3</div>
              <div>
                <h4 class="text-xl font-bold text-gray-900 mb-2">Describe Your Daily Routine</h4>
                <p class="text-gray-700 text-lg">Use present tense verbs: <strong>Me despierto</strong> (I wake up), <strong>Desayuno</strong> (I have breakfast), <strong>Trabajo</strong> (I work)</p>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-yellow-500">
          Week 2: Expanding Your Vocabulary
        </h2>

        <p class="text-xl mb-6">
          Now that you have the basics, it's time to expand into practical, everyday vocabulary. Focus on topics you'll actually use in conversations.
        </p>

        <div class="bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 p-1 rounded-3xl shadow-2xl my-8">
          <div class="bg-white p-8 rounded-3xl">
            <h3 class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500 mb-6 font-display">Days 8-10: Food and Restaurants</h3>
            <div class="grid md:grid-cols-3 gap-6">
              <div class="text-center p-6 bg-red-50 rounded-2xl">
                <h4 class="font-bold text-lg mb-2">Food Vocabulary</h4>
                <p class="text-gray-700">Learn 50+ essential food words: fruits, vegetables, meats, drinks</p>
              </div>
              <div class="text-center p-6 bg-orange-50 rounded-2xl">
                <h4 class="font-bold text-lg mb-2">Ordering Food</h4>
                <p class="text-gray-700"><strong>Quisiera...</strong> (I would like...), <strong>La cuenta, por favor</strong> (The bill, please)</p>
              </div>
              <div class="text-center p-6 bg-yellow-50 rounded-2xl">
                <h4 class="font-bold text-lg mb-2">Menu Items</h4>
                <p class="text-gray-700">Understand menus and ask about ingredients with confidence</p>
              </div>
            </div>
          </div>
        </div>

      

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-green-500">
          Week 3 and 4: Conversational Fluency
        </h2>

        <p class="text-xl mb-6">
          The final two weeks are about putting everything together and building real conversational skills.
        </p>

        <div class="bg-gradient-to-r from-green-500 to-blue-500 p-8 rounded-3xl text-white my-8 shadow-2xl">
          <h3 class="text-3xl font-bold mb-6">Key Focus Areas:</h3>
          <ul class="space-y-4 text-lg">
            <li class="flex items-start">
              <span><strong>Past Tense:</strong> Talk about what you did yesterday using preterite tense</span>
            </li>
            <li class="flex items-start">
              <span><strong>Opinions:</strong> Express likes, dislikes, and preferences naturally</span>
            </li>
            <li class="flex items-start">
              <span><strong>Questions:</strong> Ask and answer common questions in real conversations</span>
            </li>
            <li class="flex items-start">
              <span><strong>Practice:</strong> 30-minute daily conversations with native speakers</span>
            </li>
          </ul>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-purple-500">
          Pro Tips for Success
        </h2>

        <div class="grid md:grid-cols-2 gap-6 my-8">
          <div class="bg-white border-2 border-gray-200 p-6 rounded-2xl hover:shadow-xl transition-shadow">
            <h4 class="text-xl font-bold text-gray-900 mb-3 text-left">Consistency Over Intensity</h4>
            <p class="text-gray-700 leading-relaxed">15 minutes every single day beats 2 hours once a week. Make it a non-negotiable habit.</p>
          </div>
          <div class="bg-white border-2 border-gray-200 p-6 rounded-2xl hover:shadow-xl transition-shadow">
            <h4 class="text-xl font-bold text-gray-900 mb-3 text-left">Speak from Day One</h4>
            <p class="text-gray-700 leading-relaxed">Don't wait until you're "ready." Start speaking immediately, mistakes and all!</p>
          </div>
          <div class="bg-white border-2 border-gray-200 p-6 rounded-2xl hover:shadow-xl transition-shadow">
            <h4 class="text-xl font-bold text-gray-900 mb-3 text-left">Use Technology</h4>
            <p class="text-gray-700 leading-relaxed">Language apps, YouTube channels, podcasts - leverage every tool available.</p>
          </div>
          <div class="bg-white border-2 border-gray-200 p-6 rounded-2xl hover:shadow-xl transition-shadow">
            <h4 class="text-xl font-bold text-gray-900 mb-3 text-left">Learn Through Music</h4>
            <p class="text-gray-700 leading-relaxed">Spanish songs help you remember vocabulary and improve pronunciation naturally.</p>
          </div>
        </div>

        <div class="bg-gradient-to-br from-blue-50 to-purple-50 border-l-8 border-blue-500 p-8 rounded-2xl my-12 shadow-xl">
          <h3 class="text-3xl font-bold mb-6 text-gray-900">Your Success Formula</h3>
          <div class="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div class="text-5xl font-bold text-red-600 mb-2">15-30</div>
              <p class="text-lg text-gray-700">Minutes daily practice</p>
            </div>
            <div>
              <div class="text-5xl font-bold text-orange-500 mb-2">100+</div>
              <p class="text-lg text-gray-700">Essential phrases learned</p>
            </div>
            <div>
              <div class="text-5xl font-bold text-yellow-600 mb-2">30</div>
              <p class="text-lg text-gray-700">Days to fluency</p>
            </div>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-red-500">
          Ready to Start Your Journey?
        </h2>

        <p class="text-xl mb-6">
          Following this 30-day plan will give you a solid foundation in Spanish conversation. But to truly accelerate your learning, 
          you need the right tools and resources.
        </p>

        <div class="bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 p-10 rounded-3xl text-white my-12 shadow-2xl">
          <h3 class="text-4xl font-bold mb-6">Our Complete Spanish Learning Packs</h3>
          <p class="text-xl mb-8">
            We've created comprehensive learning packs that include everything you need to master Spanish in 30 days and beyond:
          </p>
          
          <div class="grid md:grid-cols-2 gap-6 mb-8">
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4">What's Included:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>40-100 Song Translations</strong> with lyrics and grammar</li>
                <li>• <strong>Interactive Games</strong> for PC and mobile</li>
                <li>• <strong>25+ Quizzes and Exercises</strong> with instant feedback</li>
                <li>• <strong>Graded Ebooks and Audiobooks</strong> (A1-C1 levels)</li>
                <li>• <strong>25 Film Recommendations</strong> with study guides</li>
              </ul>
            </div>
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4">Plus:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Recipe Ebooks</strong> from Spanish-speaking countries</li>
                <li>• <strong>Travel Guides</strong> with cultural context</li>
                <li>• <strong>Tutor Support</strong> from certified teachers</li>
                <li>• <strong>Face-to-Face Classes</strong> (optional)</li>
                <li>• <strong>Lifetime Access</strong> to all materials</li>
              </ul>
            </div>
          </div>

          <div class="text-center">
            <p class="text-2xl mb-6 font-bold text-white">
              Over 150,000 books sold | 1,200+ bundle packs purchased
            </p>
            <a href="/store" class="inline-block bg-white text-red-600 px-12 py-5 rounded-2xl font-bold text-2xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
              View Our Learning Packs →
            </a>
          </div>
        </div>

        <p class="text-xl text-gray-700 mb-6">
          Don't just learn Spanish—<strong>experience it</strong> through music, culture, food, and real conversations. 
          Our packs are designed to make learning fun, engaging, and effective.
        </p>

        <div class="bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 p-12 rounded-3xl my-16 shadow-2xl text-white relative overflow-hidden">
          <div class="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="relative z-10">
            <div class="text-center mb-8">
              <h3 class="text-4xl font-bold mb-4 text-white">FREE DOWNLOAD!</h3>
              <p class="text-2xl mb-2 text-white">30-Day Spanish Learning Roadmap</p>
              <p class="text-xl text-white/90">Complete guide with daily exercises + bonus vocabulary lists</p>
            </div>
            <div class="flex justify-center">
              <a href="/images/products/1.png" download="Spanish-30-Day-Roadmap.png" class="inline-flex items-center bg-white text-purple-600 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
                Download FREE Guide (PDF)
              </a>
            </div>
            <p class="text-center mt-6 text-white/80">No email required  |  Instant download  |  Print-friendly format</p>
          </div>
        </div>

        <p class="text-2xl font-bold text-center text-gray-900 mt-12">
          ¡Buena suerte en tu viaje de aprendizaje!
        </p>
      `
    },
    '2': {
      id: 2,
      title: "Spanish Music: Your Secret Learning Weapon",
      subtitle: "How Songs Accelerate Language Acquisition",
      author: "Carlos Rodríguez",
      authorTitle: "Music Educator and Polyglot",
      date: "January 8, 2024",
      readTime: "6 min read",
      image: "/images/spanish4.webp",
      category: "Music and Culture",
      content: `
        <p class="text-2xl leading-relaxed text-gray-700 font-light mb-8">
          What if I told you that listening to music could <strong class="text-red-600">triple your learning speed</strong>? 
          Scientific research proves that music activates multiple brain regions simultaneously, creating stronger neural 
          connections than traditional study methods.
        </p>

        <div class="bg-gradient-to-br from-blue-50 to-cyan-50 border-l-8 border-blue-500 p-8 rounded-2xl my-12 shadow-xl">
          <div class="text-center">
            <div class="text-6xl mb-4"></div>
            <h3 class="text-3xl font-bold mb-4">The Science is Clear</h3>
            <p class="text-xl">Students who learn with music retain <span class="text-red-600 font-bold text-3xl">3x more vocabulary</span> 
            and improve pronunciation <span class="text-orange-500 font-bold text-3xl">2x faster</span> than traditional methods.</p>
          </div>
        </div>

    

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-purple-500">
          Why Music Works Like Magic
        </h2>

        <div class="grid md:grid-cols-2 gap-6 my-8">
          <div class="bg-gradient-to-br from-purple-500 to-pink-500 p-8 rounded-3xl text-white shadow-2xl transform hover:scale-105 transition-transform">
            <div class="text-5xl mb-4"></div>
            <h4 class="text-2xl font-bold mb-3">Memory Enhancement</h4>
            <p class="text-lg">Melodies create "memory hooks" that help you remember words and phrases 10x longer than flashcards.</p>
          </div>
          <div class="bg-gradient-to-br from-blue-500 to-cyan-500 p-8 rounded-3xl text-white shadow-2xl transform hover:scale-105 transition-transform">
            <div class="text-5xl mb-4"></div>
            <h4 class="text-2xl font-bold mb-3">Perfect Pronunciation</h4>
            <p class="text-lg">Singing along naturally improves your accent, rhythm, and intonation—without boring drills.</p>
          </div>
          <div class="bg-gradient-to-br from-green-500 to-teal-500 p-8 rounded-3xl text-white shadow-2xl transform hover:scale-105 transition-transform">
            <div class="text-5xl mb-4"></div>
            <h4 class="text-2xl font-bold mb-3">Cultural Immersion</h4>
            <p class="text-lg">Songs expose you to authentic language, slang, and cultural references you won't find in textbooks.</p>
          </div>
          <div class="bg-gradient-to-br from-orange-500 to-red-500 p-8 rounded-3xl text-white shadow-2xl transform hover:scale-105 transition-transform">
            <div class="text-5xl mb-4"></div>
            <h4 class="text-2xl font-bold mb-3">Emotional Connection</h4>
            <p class="text-lg">Music creates positive associations with learning, making you actually WANT to study Spanish.</p>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-red-500">
          The 5-Step Music Learning Method
        </h2>

        <div class="space-y-6 my-8">
          <div class="flex items-start bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-2xl border-l-8 border-red-500">
            <div class="flex-shrink-0 w-16 h-16 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">1</div>
            <div>
              <h4 class="text-2xl font-bold text-gray-900 mb-2">Choose the Right Songs</h4>
              <p class="text-lg text-gray-700">Start with slower ballads that have clear pronunciation. As you progress, move to faster-paced songs. Beginners: ballads. Intermediate: pop. Advanced: rap!</p>
            </div>
          </div>

          <div class="flex items-start bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-2xl border-l-8 border-orange-500">
            <div class="flex-shrink-0 w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">2</div>
            <div>
              <h4 class="text-2xl font-bold text-gray-900 mb-2">Read the Lyrics First</h4>
              <p class="text-lg text-gray-700">Always have lyrics in front of you. Read in Spanish first, then check English translation. Notice patterns and new vocabulary.</p>
            </div>
          </div>

          <div class="flex items-start bg-gradient-to-r from-yellow-50 to-green-50 p-6 rounded-2xl border-l-8 border-yellow-600">
            <div class="flex-shrink-0 w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">3</div>
            <div>
              <h4 class="text-2xl font-bold text-gray-900 mb-2">Listen Actively</h4>
              <p class="text-lg text-gray-700">Don't just play music in the background. Focus on the words, try to catch individual phrases, notice how words connect in natural speech.</p>
            </div>
          </div>

          <div class="flex items-start bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-2xl border-l-8 border-green-500">
            <div class="flex-shrink-0 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">4</div>
            <div>
              <h4 class="text-2xl font-bold text-gray-900 mb-2">Sing Along!</h4>
              <p class="text-lg text-gray-700">This is where the magic happens! Singing improves pronunciation, rhythm, and confidence. Don't worry about sounding perfect—just enjoy it!</p>
            </div>
          </div>

          <div class="flex items-start bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl border-l-8 border-blue-500">
            <div class="flex-shrink-0 w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">5</div>
            <div>
              <h4 class="text-2xl font-bold text-gray-900 mb-2">Analyze the Grammar</h4>
              <p class="text-lg text-gray-700">Pick out interesting phrases and study their structure. Songs often use colloquial expressions you won't find in textbooks!</p>
            </div>
          </div>
        </div>

      

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-pink-500">
          Top 10 Songs for Spanish Learners
        </h2>

        <div class="bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 p-10 rounded-3xl text-white my-8 shadow-2xl">
          <h3 class="text-4xl font-bold mb-8 text-center text-yellow-300"> Your Essential Playlist</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-white/10 backdrop-blur p-6 rounded-2xl border border-white/20">
              <div class="flex items-center mb-3">
                <span class="text-3xl mr-3"></span>
                <div>
                  <h4 class="text-xl font-bold">Bésame Mucho</h4>
                  <p class="text-sm text-gray-300">Consuelo Velázquez</p>
                </div>
              </div>
              <p class="text-sm">Classic, slow, crystal-clear pronunciation. Perfect for beginners!</p>
            </div>

            <div class="bg-white/10 backdrop-blur p-6 rounded-2xl border border-white/20">
              <div class="flex items-center mb-3">
                <span class="text-3xl mr-3"></span>
                <div>
                  <h4 class="text-xl font-bold">La Bamba</h4>
                  <p class="text-sm text-gray-300">Ritchie Valens</p>
                </div>
              </div>
              <p class="text-sm">Fun, repetitive, incredibly catchy. You'll memorize it in one day!</p>
            </div>

            <div class="bg-white/10 backdrop-blur p-6 rounded-2xl border border-white/20">
              <div class="flex items-center mb-3">
                <span class="text-3xl mr-3"></span>
                <div>
                  <h4 class="text-xl font-bold">Vivir Mi Vida</h4>
                  <p class="text-sm text-gray-300">Marc Anthony</p>
                </div>
              </div>
              <p class="text-sm">Upbeat salsa with motivational lyrics. Great for intermediate learners.</p>
            </div>

            <div class="bg-white/10 backdrop-blur p-6 rounded-2xl border border-white/20">
              <div class="flex items-center mb-3">
                <span class="text-3xl mr-3"></span>
                <div>
                  <h4 class="text-xl font-bold">Bailando</h4>
                  <p class="text-sm text-gray-300">Enrique Iglesias</p>
                </div>
              </div>
              <p class="text-sm">Modern pop hit with clear vocals. Perfect for practicing present tense!</p>
            </div>

            <div class="bg-white/10 backdrop-blur p-6 rounded-2xl border border-white/20">
              <div class="flex items-center mb-3">
                <span class="text-3xl mr-3"></span>
                <div>
                  <h4 class="text-xl font-bold">Despacito</h4>
                  <p class="text-sm text-gray-300">Luis Fonsi</p>
                </div>
              </div>
              <p class="text-sm">Global phenomenon! Great for pronunciation and rhythm practice.</p>
            </div>

            <div class="bg-white/10 backdrop-blur p-6 rounded-2xl border border-white/20">
              <div class="flex items-center mb-3">
                <span class="text-3xl mr-3"></span>
                <div>
                  <h4 class="text-xl font-bold">Me Gustas Tú</h4>
                  <p class="text-sm text-gray-300">Manu Chao</p>
                </div>
              </div>
              <p class="text-sm">Simple vocabulary, super repetitive. Beginners love this one!</p>
            </div>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-orange-500">
          Learn Spanish Through Our Music Collections
        </h2>

        <p class="text-xl mb-6">
          Want to supercharge your Spanish learning with music? We've created comprehensive song collections with everything you need.
        </p>

        <div class="bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 p-10 rounded-3xl text-white my-12 shadow-2xl">
          <h3 class="text-4xl font-bold mb-6"> Our Music Learning Packs Include:</h3>
          
          <div class="grid md:grid-cols-2 gap-8 mb-8">
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4"> Song Collections:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>40-100 Spanish Songs</strong> with complete lyrics</li>
                <li>• <strong>Full English Translations</strong> line by line</li>
                <li>• <strong>Grammar Explanations</strong> for every phrase</li>
                <li>• <strong>Vocabulary Lists</strong> with definitions</li>
                <li>• <strong>Audio Lessons</strong> with pronunciation guides</li>
              </ul>
            </div>
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4"> Plus More:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>25 Film Recommendations</strong> with study guides</li>
                <li>• <strong>Interactive Quizzes</strong> based on songs</li>
                <li>• <strong>Cultural Context</strong> for each song</li>
                <li>• <strong>Curated Playlists</strong> by level (A1-C2)</li>
                <li>• <strong>Lifetime Access</strong> to all materials</li>
              </ul>
            </div>
          </div>

          <div class="bg-white/20 backdrop-blur p-6 rounded-2xl mb-8">
            <h4 class="text-2xl font-bold mb-4"> Different Genres Covered:</h4>
            <p class="text-lg mb-4">From traditional ballads to modern reggaeton, flamenco to rock en español—we cover all genres to match your musical taste!</p>
            <div class="flex flex-wrap gap-3">
              <span class="bg-white/30 px-4 py-2 rounded-full">Pop</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Rock</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Salsa</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Reggaeton</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Flamenco</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Ballads</span>
            </div>
          </div>

          <div class="text-center">
            <p class="text-2xl mb-6 font-bold">
               Join 150,000+ students learning Spanish through music!
            </p>
            <a href="/store" class="inline-block bg-white text-red-600 px-12 py-5 rounded-2xl font-bold text-2xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
              Explore Music Learning Packs →
            </a>
          </div>
        </div>

        <p class="text-xl text-gray-700 mb-6">
          Music isn't just entertainment—it's a powerful learning tool. Our packs make it easy to learn Spanish while enjoying 
          the beautiful music of Spanish-speaking cultures.
        </p>

        <div class="bg-gradient-to-br from-indigo-600 via-purple-500 to-pink-500 p-12 rounded-3xl my-16 shadow-2xl text-white relative overflow-hidden">
          <div class="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="relative z-10">
            <div class="text-center mb-8">
              <div class="text-6xl mb-4"></div>
              <h3 class="text-4xl font-bold mb-4">FREE DOWNLOAD!</h3>
              <p class="text-2xl mb-2">Top 50 Spanish Songs for Learning</p>
              <p class="text-xl text-white/90">Curated playlist with lyrics + translations + grammar notes</p>
            </div>
            <div class="flex justify-center">
              <a href="/images/products/2.png" download="Spanish-Music-Learning-Guide.png" class="inline-flex items-center bg-white text-indigo-600 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
                <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download FREE Playlist (PDF)
              </a>
            </div>
            <p class="text-center mt-6 text-white/80">• No email required  • Instant download  • Spotify links included</p>
          </div>
        </div>

        <p class="text-2xl font-bold text-center text-gray-900 mt-12">
          ¡Que disfrutes la música! 
        </p>
      `
    },
    '3': {
      id: 3,
      title: "Travel Spanish: Essential Phrases",
      subtitle: "Everything You Need for Your Trip",
      author: "Ana Martínez",
      authorTitle: "Travel Expert and Language Coach",
      date: "January 5, 2024",
      readTime: "7 min read",
      image: "/images/spanish5.webp",
      category: "Travel",
      content: `
        <p class="text-2xl leading-relaxed text-gray-700 font-light mb-8">
          Planning a trip to Spain or Latin America? Knowing even <strong class="text-red-600">basic Spanish phrases</strong> 
          can completely transform your travel experience. You'll connect with locals, get better recommendations, and handle 
          any situation with confidence.
        </p>

        <div class="my-12">
          <img src="/images/spanish6.webp" alt="Travel Spanish" class="w-full rounded-3xl shadow-2xl" />
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-blue-500">
          At the Airport 
        </h2>

        <div class="bg-gradient-to-br from-blue-500 to-purple-600 p-8 rounded-3xl text-white my-8 shadow-2xl">
          <h3 class="text-3xl font-bold mb-6">Essential Airport Phrases</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-xl font-bold mb-4 text-yellow-200">Check-in and Security</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>¿Dónde está el mostrador?</strong><br/><span class="text-sm text-blue-100">Where is the counter?</span></li>
                <li>• <strong>Mi vuelo sale a las...</strong><br/><span class="text-sm text-blue-100">My flight leaves at...</span></li>
                <li>• <strong>¿Cuál es la puerta?</strong><br/><span class="text-sm text-blue-100">Which is the gate?</span></li>
                <li>• <strong>¿Dónde está la recogida de equipaje?</strong><br/><span class="text-sm text-blue-100">Where is baggage claim?</span></li>
              </ul>
            </div>
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-xl font-bold mb-4 text-yellow-200">Immigration</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Estoy aquí de turismo</strong><br/><span class="text-sm text-blue-100">I'm here as a tourist</span></li>
                <li>• <strong>Voy a quedarme... días</strong><br/><span class="text-sm text-blue-100">I'm staying... days</span></li>
                <li>• <strong>Nada que declarar</strong><br/><span class="text-sm text-blue-100">Nothing to declare</span></li>
              </ul>
            </div>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-orange-500">
          At Hotels and Restaurants
        </h2>

        <div class="grid md:grid-cols-2 gap-8 my-8">
          <div class="bg-gradient-to-br from-orange-400 to-red-500 p-8 rounded-3xl text-white shadow-2xl">
            <h3 class="text-3xl font-bold mb-6 font-display">Hotel Essentials</h3>
            <ul class="space-y-4 text-lg">
              <li class="flex items-start">
                <div>
                  <strong>Tengo una reserva</strong><br/>
                  <span class="text-sm text-orange-100">I have a reservation</span>
                </div>
              </li>
              <li class="flex items-start">
                <div>
                  <strong>¿Cuánto cuesta por noche?</strong><br/>
                  <span class="text-sm text-orange-100">How much per night?</span>
                </div>
              </li>
              <li class="flex items-start">
                <div>
                  <strong>¿Tiene WiFi?</strong><br/>
                  <span class="text-sm text-orange-100">Do you have WiFi?</span>
                </div>
              </li>
              <li class="flex items-start">
                <div>
                  <strong>Necesito más toallas</strong><br/>
                  <span class="text-sm text-orange-100">I need more towels</span>
                </div>
              </li>
            </ul>
          </div>
          <div class="bg-gradient-to-br from-green-400 to-teal-500 p-8 rounded-3xl text-white shadow-2xl">
            <h3 class="text-3xl font-bold mb-6 font-display">Restaurant Phrases</h3>
            <ul class="space-y-4 text-lg">
              <li class="flex items-start">
                <div>
                  <strong>Una mesa para dos</strong><br/>
                  <span class="text-sm text-green-100">A table for two</span>
                </div>
              </li>
              <li class="flex items-start">
                <div>
                  <strong>¿Qué me recomienda?</strong><br/>
                  <span class="text-sm text-green-100">What do you recommend?</span>
                </div>
              </li>
              <li class="flex items-start">
                <div>
                  <strong>La cuenta, por favor</strong><br/>
                  <span class="text-sm text-green-100">The bill, please</span>
                </div>
              </li>
              <li class="flex items-start">
                <div>
                  <strong>Soy vegetariano/a</strong><br/>
                  <span class="text-sm text-green-100">I'm vegetarian</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

    

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-yellow-500">
          Getting Around
        </h2>

        <div class="bg-white border-4 border-yellow-400 p-10 rounded-3xl my-8 shadow-2xl">
          <h3 class="text-4xl font-bold text-gray-900 mb-8 text-center">Navigation Essentials</h3>
          <div class="grid md:grid-cols-3 gap-6 mb-8">
            <div class="text-center p-6 bg-yellow-50 rounded-2xl">
              <div class="text-5xl mb-4"></div>
              <h4 class="text-xl font-bold mb-2">A la derecha</h4>
              <p class="text-gray-600">To the right</p>
            </div>
            <div class="text-center p-6 bg-orange-50 rounded-2xl">
              <div class="text-5xl mb-4"></div>
              <h4 class="text-xl font-bold mb-2">A la izquierda</h4>
              <p class="text-gray-600">To the left</p>
            </div>
            <div class="text-center p-6 bg-red-50 rounded-2xl">
              <div class="text-5xl mb-4"></div>
              <h4 class="text-xl font-bold mb-2">Todo recto</h4>
              <p class="text-gray-600">Straight ahead</p>
            </div>
          </div>
          <div class="p-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl text-white">
            <h4 class="text-2xl font-bold mb-4">Key Questions:</h4>
            <ul class="space-y-2 text-lg">
              <li>• <strong>¿Dónde está...?</strong> - Where is...?</li>
              <li>• <strong>¿Cómo llego a...?</strong> - How do I get to...?</li>
              <li>• <strong>¿Está lejos?</strong> - Is it far?</li>
              <li>• <strong>¿Puedo ir caminando?</strong> - Can I walk there?</li>
            </ul>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-red-500">
          Emergency Phrases
        </h2>

        <div class="bg-gradient-to-br from-red-100 to-orange-100 border-l-8 border-red-600 p-8 rounded-2xl my-8 shadow-xl">
          <h3 class="text-2xl font-bold text-red-800 mb-6">Important - Save These!</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <h4 class="text-xl font-bold mb-3 text-gray-900">Medical:</h4>
              <ul class="space-y-2 text-lg text-gray-800">
                <li>• <strong>Necesito un médico</strong> - I need a doctor</li>
                <li>• <strong>¿Dónde está el hospital?</strong> - Where is the hospital?</li>
                <li>• <strong>Me duele...</strong> - My... hurts</li>
                <li>• <strong>Soy alérgico/a a...</strong> - I'm allergic to...</li>
              </ul>
            </div>
            <div>
              <h4 class="text-xl font-bold mb-3 text-gray-900">Safety:</h4>
              <ul class="space-y-2 text-lg text-gray-800">
                <li>• <strong>¡Ayuda!</strong> - Help!</li>
                <li>• <strong>¡Policía!</strong> - Police!</li>
                <li>• <strong>Perdí mi pasaporte</strong> - I lost my passport</li>
                <li>• <strong>¿Dónde está la embajada?</strong> - Where is the embassy?</li>
              </ul>
            </div>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-green-500">
          Your Complete Travel Spanish Guide
        </h2>

        <p class="text-xl mb-6">
          These phrases will get you started, but for a truly confident travel experience, you need comprehensive preparation.
        </p>

        <div class="bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 p-10 rounded-3xl text-white my-12 shadow-2xl">
          <h3 class="text-4xl font-bold mb-6">Our Travel Spanish Packs Include:</h3>
          
          <div class="grid md:grid-cols-2 gap-8 mb-8">
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4">Complete Travel Guides:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Country-Specific Guides</strong> with cultural context</li>
                <li>• <strong>Authentic Dialogues</strong> for real situations</li>
                <li>• <strong>500+ Essential Phrases</strong> organized by topic</li>
                <li>• <strong>Pronunciation Audio</strong> for every phrase</li>
                <li>• <strong>Downloadable PDFs</strong> for offline use</li>
              </ul>
            </div>
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4">Plus:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Recipe Ebooks</strong> from each country</li>
                <li>• <strong>Cultural Tips</strong> and local customs</li>
                <li>• <strong>Interactive Quizzes</strong> to practice</li>
                <li>• <strong>Emergency Phrase Cards</strong> to print</li>
                <li>• <strong>Mobile-Friendly</strong> access anywhere</li>
              </ul>
            </div>
          </div>

          <div class="bg-white/20 backdrop-blur p-6 rounded-2xl mb-8">
            <h4 class="text-2xl font-bold mb-4">Countries Covered:</h4>
            <div class="flex flex-wrap gap-3">
              <span class="bg-white/30 px-4 py-2 rounded-full">Spain</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Mexico</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Argentina</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Colombia</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Peru</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">Chile</span>
              <span class="bg-white/30 px-4 py-2 rounded-full">And more!</span>
            </div>
          </div>

          <div class="text-center">
            <p class="text-2xl mb-6 font-bold text-white">
              Perfect for beginners planning their first trip!
            </p>
            <a href="/store" class="inline-block bg-white text-red-600 px-12 py-5 rounded-2xl font-bold text-2xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
              Get Your Travel Guide →
            </a>
          </div>
        </div>

        <p class="text-xl text-gray-700 mb-6">
          Don't let language barriers hold you back from amazing travel experiences. Our comprehensive travel packs prepare 
          you for every situation you'll encounter on your journey.
        </p>

        <div class="bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 p-12 rounded-3xl my-16 shadow-2xl text-white relative overflow-hidden">
          <div class="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="relative z-10">
            <div class="text-center mb-8">
              <h3 class="text-4xl font-bold mb-4 text-white">FREE DOWNLOAD!</h3>
              <p class="text-2xl mb-2 text-white">Essential Travel Spanish Phrasebook</p>
              <p class="text-xl text-white/90">500+ phrases for airports, hotels, restaurants and emergencies</p>
            </div>
            <div class="flex justify-center">
              <a href="/images/products/3.png" download="Travel-Spanish-Phrasebook.png" class="inline-flex items-center bg-white text-blue-600 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
                Download FREE Phrasebook (PDF)
              </a>
            </div>
            <p class="text-center mt-6 text-white/80">No email required  |  Instant download  |  Pocket-sized format</p>
          </div>
        </div>

        <p class="text-2xl font-bold text-center text-gray-900 mt-12">
          ¡Buen viaje!
        </p>
      `
    },
    '4': {
      id: 4,
      title: "Spanish Grammar Made Simple",
      subtitle: "Master the Subjunctive Mood",
      author: "Diego Fernández",
      authorTitle: "Grammar Expert and Certified Teacher",
      date: "January 3, 2024",
      readTime: "9 min read",
      image: "/images/spanish7.webp",
      category: "Grammar",
      content: `
        <p class="text-2xl leading-relaxed text-gray-700 font-light mb-8">
          The subjunctive mood is often called the <strong class="text-red-600">"final boss"</strong> of Spanish grammar. 
          But here's a secret: once you understand the logic behind it, it becomes surprisingly simple!
        </p>

        <div class="bg-gradient-to-br from-yellow-50 to-orange-50 border-l-8 border-yellow-500 p-8 rounded-2xl my-12 shadow-xl">
          <div class="text-center">
            <h3 class="text-3xl font-bold mb-4">What IS the Subjunctive?</h3>
            <p class="text-xl">The subjunctive is a <strong>mood</strong>, not a tense. It expresses things that are 
            <span class="text-orange-600 font-bold">uncertain, subjective, or hypothetical</span>—not facts.</p>
          </div>
        </div>


        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-purple-500">
          The WEIRDO Method
        </h2>

        <p class="text-xl mb-8">
          Remember this acronym and you'll always know when to use the subjunctive:
        </p>

        <div class="space-y-6 my-8">
          <div class="bg-gradient-to-r from-red-500 to-pink-500 p-8 rounded-3xl text-white shadow-2xl">
            <h4 class="text-3xl font-bold mb-4">W - Wishes and Desires</h4>
            <ul class="space-y-3 text-lg">
              <li>• <strong>Quiero que</strong> estudies - I want you to study</li>
              <li>• <strong>Espero que</strong> tengas suerte - I hope you have luck</li>
              <li>• <strong>Deseo que</strong> seas feliz - I wish you to be happy</li>
            </ul>
          </div>

          <div class="bg-gradient-to-r from-orange-500 to-yellow-500 p-8 rounded-3xl text-white shadow-2xl">
            <h4 class="text-3xl font-bold mb-4">E - Emotions</h4>
            <ul class="space-y-3 text-lg">
              <li>• <strong>Me alegra que</strong> estés aquí - I'm happy you're here</li>
              <li>• <strong>Temo que</strong> llueva - I fear it will rain</li>
              <li>• <strong>Me sorprende que</strong> no sepas - I'm surprised you don't know</li>
            </ul>
          </div>

          <div class="bg-gradient-to-r from-yellow-500 to-green-500 p-8 rounded-3xl text-white shadow-2xl">
            <h4 class="text-3xl font-bold mb-4">I - Impersonal Expressions</h4>
            <ul class="space-y-3 text-lg">
              <li>• <strong>Es importante que</strong> llegues temprano - It's important you arrive early</li>
              <li>• <strong>Es necesario que</strong> estudies - It's necessary you study</li>
              <li>• <strong>Es posible que</strong> venga - It's possible he'll come</li>
            </ul>
          </div>

          <div class="bg-gradient-to-r from-green-500 to-blue-500 p-8 rounded-3xl text-white shadow-2xl">
            <h4 class="text-3xl font-bold mb-4">R - Recommendations</h4>
            <ul class="space-y-3 text-lg">
              <li>• <strong>Recomiendo que</strong> vayas - I recommend you go</li>
              <li>• <strong>Sugiero que</strong> hables con él - I suggest you talk to him</li>
              <li>• <strong>Te pido que</strong> me ayudes - I ask you to help me</li>
            </ul>
          </div>

          <div class="bg-gradient-to-r from-blue-500 to-purple-500 p-8 rounded-3xl text-white shadow-2xl">
            <h4 class="text-3xl font-bold mb-4">D - Doubt and Denial</h4>
            <ul class="space-y-3 text-lg">
              <li>• <strong>Dudo que</strong> sea verdad - I doubt it's true</li>
              <li>• <strong>No creo que</strong> venga - I don't think he'll come</li>
              <li>• <strong>Niego que</strong> sea culpable - I deny he's guilty</li>
            </ul>
          </div>

          <div class="bg-gradient-to-r from-purple-500 to-pink-500 p-8 rounded-3xl text-white shadow-2xl">
            <h4 class="text-3xl font-bold mb-4">O - Ojalá (I hope/wish)</h4>
            <ul class="space-y-3 text-lg">
              <li>• <strong>Ojalá que</strong> tengas razón - I hope you're right</li>
              <li>• <strong>Ojalá</strong> pueda ir - I hope I can go</li>
            </ul>
          </div>
        </div>

        
        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-orange-500">
          How to Form the Subjunctive
        </h2>

        <div class="bg-gradient-to-br from-gray-100 to-gray-200 p-10 rounded-3xl my-8 shadow-2xl">
          <h3 class="text-3xl font-bold text-gray-900 mb-8 text-center">3 Simple Steps</h3>
          <div class="space-y-6">
            <div class="bg-white p-6 rounded-2xl shadow-lg border-l-8 border-red-500">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">1</div>
                <h4 class="text-2xl font-bold">Start with YO form</h4>
              </div>
              <p class="text-lg text-gray-700 ml-16">hablar → <strong>hablo</strong></p>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-lg border-l-8 border-orange-500">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">2</div>
                <h4 class="text-2xl font-bold">Drop the -O</h4>
              </div>
              <p class="text-lg text-gray-700 ml-16">hablo → <strong>habl-</strong></p>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-lg border-l-8 border-yellow-600">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">3</div>
                <h4 class="text-2xl font-bold">Add OPPOSITE endings</h4>
              </div>
              <p class="text-lg text-gray-700 ml-16">habl- → <strong>hable, hables, hable, hablemos, habléis, hablen</strong></p>
            </div>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-green-500">
          Common Mistakes to Avoid
        </h2>

        <div class="grid md:grid-cols-2 gap-6 my-8">
          <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-xl">
            <h4 class="text-xl font-bold text-red-700 mb-3 text-left">Wrong:</h4>
            <p class="text-lg text-gray-800">Dudo que <strong>viene</strong></p>
            <p class="text-sm text-gray-600 mt-2">Using indicative after doubt</p>
          </div>
          <div class="bg-green-50 border-l-4 border-green-500 p-6 rounded-xl">
            <h4 class="text-xl font-bold text-green-700 mb-3 text-left">Correct:</h4>
            <p class="text-lg text-gray-800">Dudo que <strong>venga</strong></p>
            <p class="text-sm text-gray-600 mt-2">Subjunctive after expressions of doubt</p>
          </div>
        </div>

        <h2 class="text-4xl font-bold text-gray-900 mt-16 mb-8 pb-4 border-b-4 border-red-500">
          Master Grammar with Our Complete Packs
        </h2>

        <p class="text-xl mb-6">
          Understanding the subjunctive is just the beginning. To truly master Spanish grammar, you need comprehensive practice and expert guidance.
        </p>

        <div class="bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 p-10 rounded-3xl text-white my-12 shadow-2xl">
          <h3 class="text-4xl font-bold mb-6">Our Grammar Learning Packs Include:</h3>
          
          <div class="grid md:grid-cols-2 gap-8 mb-8">
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4">Complete Grammar Guides:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>All Verb Tenses</strong> explained simply</li>
                <li>• <strong>Interactive Exercises</strong> with instant feedback</li>
                <li>• <strong>25+ Comprehensive Quizzes</strong></li>
                <li>• <strong>Real-World Examples</strong> in context</li>
                <li>• <strong>Progressive Difficulty</strong> (A1-C2)</li>
              </ul>
            </div>
            <div class="bg-white/20 backdrop-blur p-6 rounded-2xl">
              <h4 class="text-2xl font-bold mb-4">Plus:</h4>
              <ul class="space-y-3 text-lg">
                <li>• <strong>Video Lessons</strong> from expert teachers</li>
                <li>• <strong>Practice Worksheets</strong> (downloadable)</li>
                <li>• <strong>Grammar Games</strong> for fun learning</li>
                <li>• <strong>Tutor Support</strong> for questions</li>
                <li>• <strong>Progress Tracking</strong> system</li>
              </ul>
            </div>
          </div>

          <div class="text-center">
            <p class="text-2xl mb-6 font-bold text-white">
              From beginner to advanced - we've got you covered!
            </p>
            <a href="/store" class="inline-block bg-white text-red-600 px-12 py-5 rounded-2xl font-bold text-2xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
              Explore Grammar Packs →
            </a>
          </div>
        </div>

        <p class="text-xl text-gray-700 mb-6">
          Grammar doesn't have to be boring! Our interactive packs make learning Spanish grammar engaging, effective, and even fun.
        </p>

        <div class="bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 p-12 rounded-3xl my-16 shadow-2xl text-white relative overflow-hidden">
          <div class="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div class="relative z-10">
            <div class="text-center mb-8">
              <h3 class="text-4xl font-bold mb-4 text-white">FREE DOWNLOAD!</h3>
              <p class="text-2xl mb-2 text-white">Spanish Grammar Cheat Sheet</p>
              <p class="text-xl text-white/90">WEIRDO method + verb conjugations + practice exercises</p>
            </div>
            <div class="flex justify-center">
              <a href="/images/products/4.png" download="Spanish-Grammar-Guide.png" class="inline-flex items-center bg-white text-green-600 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
                Download FREE Cheat Sheet (PDF)
              </a>
            </div>
            <p class="text-center mt-6 text-white/80">No email required  |  Instant download  |  Printable reference</p>
          </div>
        </div>

        <p class="text-2xl font-bold text-center text-gray-900 mt-12">
          ¡Espero que este artículo te ayude!
        </p>
      `
    }
  };

  const article = articles[id] || articles['1'];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;600;700;800&display=swap');
        
        .article-title {
          font-family: 'Playfair Display', serif;
        }
        
        .article-body {
          font-family: 'Inter', sans-serif;
        }
      `}</style>

      <Header />

      {/* Article Hero - Larger and More Dramatic */}
      <section className="relative h-[600px] overflow-hidden">
        <img 
          src={article.image} 
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
            <Link to="/blog" className="inline-flex items-center text-yellow-300 hover:text-yellow-200 mb-6 text-lg font-semibold">
              ← Back to Blog
            </Link>
            <div className="mb-4">
              <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                {article.category}
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-2xl text-white article-title leading-tight">
              {article.title}
            </h1>
            <p className="text-2xl md:text-3xl text-yellow-200 mb-6 drop-shadow-lg article-title font-semibold">
              {article.subtitle}
            </p>
            <div className="flex items-center space-x-6 text-white text-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mr-3 font-bold text-xl">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <div className="font-bold">{article.author}</div>
                  <div className="text-sm text-gray-300">{article.authorTitle}</div>
                </div>
              </div>
              <span className="text-gray-300">•</span>
              <span>{article.date}</span>
              <span className="text-gray-300">•</span>
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                {article.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content - Beautiful Typography */}
      <article className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="article-body"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {/* CTA Section - More Attractive */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 article-title drop-shadow-lg">
            Ready to Start Your Spanish Journey?
          </h2>
          <p className="text-xl md:text-2xl text-white/95 mb-10 max-w-3xl mx-auto drop-shadow">
            Join over 10,000 students who are mastering Spanish with our proven, fun, and effective methods
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/free-ebook" className="bg-white text-red-600 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105">
              FREE EBOOK
            </Link>
            <Link to="/store" className="bg-white/20 backdrop-blur-lg text-white border-4 border-white px-10 py-5 rounded-2xl font-bold text-xl hover:bg-white/30 transition-all shadow-2xl transform hover:scale-105">
              View All Products
            </Link>
          </div>
          <p className="text-white/90 mt-8 text-lg">
            No credit card required  |  Cancel anytime  |  30-day money-back guarantee
          </p>
        </div>
      </section>

      {/* Shared Footer */}
      <Footer />
    </div>
  );
}

export default ArticlePage;
